import React, { Children } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { Heart, Shield, Users, Lightbulb, ExternalLink } from 'lucide-react';
export const About = () => {
  return <div className="pt-20" data-id="element-212">
      {/* Hero */}
      <div className="bg-[#F2F0E7] py-20 md:py-32" data-id="element-213">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-214">
          <h1 className="text-4xl md:text-6xl font-bold text-[#1C274C] mb-6" data-id="element-215">
            About Us
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-216">
            Making Artificial Intelligence accessible, ethical, and impactful
            for African children and their communities.
          </p>
        </div>
      </div>

      {/* Who We Are */}
      <Section data-id="element-217">
        <div className="max-w-4xl mx-auto" data-id="element-218">
          <h2 className="text-3xl font-bold text-[#1C274C] mb-6" data-id="element-219">Who We Are</h2>
          <div className="space-y-4 text-lg text-gray-600 leading-relaxed" data-id="element-220">
            <p data-id="element-221">
              AI For Kids Africa is a non-profit organization committed to
              making Artificial Intelligence accessible, ethical, and impactful
              for African children and their communities.
            </p>
            <p data-id="element-222">
              As AI continues to shape education, work, and everyday life, we
              believe African children deserve more than just access to
              technology — they deserve the knowledge, guidance, and confidence
              to understand and shape it.
            </p>
          </div>
        </div>
      </Section>

      {/* Why We Exist */}
      <Section background="gray" data-id="element-223">
        <div className="max-w-4xl mx-auto" data-id="element-224">
          <h2 className="text-3xl font-bold text-[#1C274C] mb-6" data-id="element-225">
            Why We Exist
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-226">
            Across Africa, children are increasingly exposed to digital tools
            powered by Artificial Intelligence, yet most lack:
          </p>
          <ul className="space-y-3 text-lg text-gray-600 mb-6" data-id="element-227">
            <li className="flex items-start" data-id="element-228">
              <span className="text-[#3AB5E8] mr-3 mt-1" data-id="element-229">•</span>
              Age-appropriate AI education
            </li>
            <li className="flex items-start" data-id="element-230">
              <span className="text-[#3AB5E8] mr-3 mt-1" data-id="element-231">•</span>
              Ethical guidance on responsible AI use
            </li>
            <li className="flex items-start" data-id="element-232">
              <span className="text-[#3AB5E8] mr-3 mt-1" data-id="element-233">•</span>
              Safe and trusted learning environments
            </li>
            <li className="flex items-start" data-id="element-234">
              <span className="text-[#3AB5E8] mr-3 mt-1" data-id="element-235">•</span>
              Support from trained educators and informed parents
            </li>
          </ul>
          <p className="text-lg text-gray-700 font-medium" data-id="element-236">
            Without intentional action, this gap risks turning African children
            into passive consumers of technology rather than creative
            contributors to the AI-driven future.
          </p>
          <p className="text-xl text-[#1C274C] font-bold mt-6" data-id="element-237">
            AI For Kids Africa exists to change that.
          </p>
        </div>
      </Section>

      {/* What Makes Us Different */}
      <Section data-id="element-238">
        <div className="max-w-4xl mx-auto" data-id="element-239">
          <h2 className="text-3xl font-bold text-[#1C274C] mb-6" data-id="element-240">
            What Makes Us Different
          </h2>
          <p className="text-lg text-gray-600 mb-6" data-id="element-241">
            We take a child-first, ethics-first approach to AI education.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-242">
            {['We introduce AI literacy before coding', 'We design learning experiences that are age-appropriate and creative', 'We empower teachers and parents, not replace them', 'We curate and recommend safe, responsible AI tools', 'We ground everything in African realities and contexts'].map((item, index) => <div key={index} className="flex items-start bg-[#F2F0E7] p-4 rounded-lg" data-id="element-243">
                <span className="text-[#389C70] mr-3 text-xl" data-id="element-244">✓</span>
                <span className="text-gray-700 font-medium" data-id="element-245">{item}</span>
              </div>)}
          </div>
          <div className="mt-8 p-6 bg-[#FC963E]/10 border-l-4 border-[#FC963E] rounded-r-lg" data-id="element-246">
            <p className="text-lg font-bold text-[#1C274C]" data-id="element-247">
              We don't chase hype or noise — we build understanding, curiosity,
              and long-term impact.
            </p>
          </div>
        </div>
      </Section>

      {/* Who We Serve */}
      <Section background="gray" data-id="element-248">
        <div className="text-center mb-12" data-id="element-249">
          <h2 className="text-3xl font-bold text-[#1C274C] mb-4" data-id="element-250">
            Who We Serve
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto" data-id="element-251">
          <div className="text-center p-6 bg-white rounded-xl" data-id="element-252">
            <div className="w-16 h-16 bg-[#3AB5E8]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-253">
              <Heart className="w-8 h-8 text-[#3AB5E8]" data-id="element-254" />
            </div>
            <h3 className="font-bold text-[#1C274C] text-lg mb-2" data-id="element-255">Children</h3>
            <p className="text-gray-600 text-sm" data-id="element-256">
              At the center of everything we do
            </p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl" data-id="element-257">
            <div className="w-16 h-16 bg-[#FC963E]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-258">
              <Users className="w-8 h-8 text-[#FC963E]" data-id="element-259" />
            </div>
            <h3 className="font-bold text-[#1C274C] text-lg mb-2" data-id="element-260">
              Teachers & Educators
            </h3>
            <p className="text-gray-600 text-sm" data-id="element-261">
              As guides and multipliers of impact
            </p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl" data-id="element-262">
            <div className="w-16 h-16 bg-[#389C70]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-263">
              <Shield className="w-8 h-8 text-[#389C70]" data-id="element-264" />
            </div>
            <h3 className="font-bold text-[#1C274C] text-lg mb-2" data-id="element-265">
              Parents & Guardians
            </h3>
            <p className="text-gray-600 text-sm" data-id="element-266">As protectors and partners</p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl" data-id="element-267">
            <div className="w-16 h-16 bg-[#F76874]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-268">
              <Lightbulb className="w-8 h-8 text-[#F76874]" data-id="element-269" />
            </div>
            <h3 className="font-bold text-[#1C274C] text-lg mb-2" data-id="element-270">
              Schools & Communities
            </h3>
            <p className="text-gray-600 text-sm" data-id="element-271">As trusted collaborators</p>
          </div>
        </div>
      </Section>

      {/* Safety & Ethics */}
      <Section className="bg-white" data-id="element-272">
        <div className="max-w-4xl mx-auto text-center bg-[#F2F0E7] p-12 rounded-3xl border-2 border-gray-200" data-id="element-273">
          <h2 className="text-3xl font-bold mb-6 text-[#1C274C]" data-id="element-274">
            Our Commitment to Safety & Ethics
          </h2>
          <p className="text-xl text-[#1C274C] mb-8 font-semibold" data-id="element-275">
            Children's safety is non-negotiable.
          </p>
          <p className="text-lg text-[#1C274C] mb-6 font-medium" data-id="element-276">
            Every program we run is guided by:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8" data-id="element-277">
            {['Ethical AI principles', 'Child safeguarding standards', 'Responsible technology use', 'Transparency and inclusion'].map((item, index) => <div key={index} className="bg-white p-4 rounded-lg border-2 border-gray-200" data-id="element-278">
                <p className="font-semibold text-[#1C274C]" data-id="element-279">{item}</p>
              </div>)}
          </div>
          <p className="text-lg text-[#1C274C] italic font-medium" data-id="element-280">
            We believe innovation must never come at the expense of a child's
            dignity, safety, or well-being.
          </p>
        </div>
      </Section>

      {/* CTA */}
      <Section className="text-center" data-id="element-281">
        <h2 className="text-3xl font-bold text-[#1C274C] mb-6" data-id="element-282">
          Join Our Mission
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-283">
          Whether you're an educator, parent, school administrator, or partner
          organization — there's a place for you in this movement.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-284">
          <a href="https://forms.gle/yiZQndnfYhzdQtX68" target="_blank" rel="noopener noreferrer" data-id="element-285">
            <Button size="lg" rightIcon={<ExternalLink className="w-5 h-5" data-id="element-287" />} data-id="element-286">
              Join the Waitlist
            </Button>
          </a>
          <Link to="/contact" data-id="element-288">
            <Button variant="secondary" size="lg" data-id="element-289">
              Get Involved
            </Button>
          </Link>
        </div>
      </Section>
    </div>;
};